// WP-3.3 (M3 PJ-1..7, S-23 `/projects`): filterable list (status/party/service line) — a
// kanban isn't the right shape here, projects don't move through one strict forward chain
// like quotations do (PJ-4's status enum allows active <-> paused, either -> completed/
// cancelled/archived). Row: title/party/earning-model chip/received%/margin badge (WP-3.6,
// server-computed on the Final Value basis — the list response, no per-row fetch)/Final Value.
import {
  formatMoney,
  type CurrencyCode,
  type ProjectEarningModel,
  type ProjectStatus,
} from '@pfos/engine';
import type { ProjectResponse, QuotationResponse } from '@pfos/shared';
import { Briefcase, Plus } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { EmptyState } from '../components/EmptyState';
import { AmountField, Field, inputClass, selectClass } from '../components/FormKit';
import { FormSheet } from '../components/FormSheet';
import { PartyPicker } from '../components/PartyPicker';
import { ApiError } from '../lib/api';
import { useParties } from '../lib/parties';
import { computeFinalValueMinor, useCreateProject, useProjects } from '../lib/projects';
import { useQuotations } from '../lib/quotations';

const STATUS_OPTIONS: { value: ProjectStatus | ''; label: string }[] = [
  { value: '', label: 'All statuses' },
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'archived', label: 'Archived' },
];

const EARNING_MODEL_OPTIONS: { value: ProjectEarningModel; label: string }[] = [
  { value: 'fixed', label: 'Fixed' },
  { value: 'hourly', label: 'Hourly' },
  { value: 'milestone', label: 'Milestone' },
  { value: 'recurring', label: 'Recurring' },
  { value: 'range_estimate', label: 'Range estimate' },
];

const EARNING_MODEL_LABELS: Record<ProjectEarningModel, string> = {
  fixed: 'Fixed',
  hourly: 'Hourly',
  milestone: 'Milestone',
  recurring: 'Recurring',
  range_estimate: 'Range',
};

function eligibleQuotations(quotations: QuotationResponse[]): QuotationResponse[] {
  return quotations.filter(
    (q) =>
      q.status === 'converted' && q.convertedToType === 'project' && q.convertedToId === undefined,
  );
}

function ProjectRow({ project }: { project: ProjectResponse }): React.JSX.Element {
  const finalValueMinor = computeFinalValueMinor(project);
  return (
    <Link
      to={`/projects/${project.id}`}
      className="flex items-center justify-between gap-3 rounded-[var(--radius-card)] border border-border bg-surface p-3 hover:border-primary"
    >
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-ink">{project.title}</p>
        <p className="truncate text-xs text-muted">
          {project.party} · <span className="capitalize">{project.status}</span>
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-3">
        <span className="rounded-full border border-border px-2 py-0.5 text-xs text-muted">
          {EARNING_MODEL_LABELS[project.earningModel]}
        </span>
        {project.receivedPercent !== undefined && (
          <span className="text-xs text-muted">
            {Math.round(project.receivedPercent * 100)}% received
          </span>
        )}
        {project.marginPercent !== undefined && (
          <span
            className={`text-xs font-medium ${project.marginPercent >= 0 ? 'text-success' : 'text-danger'}`}
          >
            M {Math.round(project.marginPercent * 100)}%
          </span>
        )}
        <span className="text-sm font-medium text-ink">
          {formatMoney({ currency: project.currency, minorUnits: finalValueMinor })}
        </span>
      </div>
    </Link>
  );
}

function NewProjectForm({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.JSX.Element {
  const [fromQuotationId, setFromQuotationId] = useState('');
  const [party, setParty] = useState('');
  const [partyId, setPartyId] = useState('');
  const [title, setTitle] = useState('');
  const [currency, setCurrency] = useState<CurrencyCode>('PKR');
  const [earningModel, setEarningModel] = useState<ProjectEarningModel>('fixed');
  const [agreedAmount, setAgreedAmount] = useState('');
  const [error, setError] = useState<string | null>(null);
  const createMutation = useCreateProject();
  const { data: parties } = useParties(false);
  const { data: quotations } = useQuotations();
  const navigate = useNavigate();
  const candidates = useMemo(() => eligibleQuotations(quotations ?? []), [quotations]);
  const selectedQuotation = candidates.find((q) => q.id === fromQuotationId);

  useEffect(() => {
    if (open) {
      setFromQuotationId('');
      setParty('');
      setPartyId('');
      setTitle('');
      setCurrency('PKR');
      setEarningModel('fixed');
      setAgreedAmount('');
      setError(null);
    }
  }, [open]);

  function applyQuotation(quotation: QuotationResponse | undefined): void {
    if (quotation === undefined) return;
    setParty(quotation.party);
    setPartyId(quotation.partyId ?? '');
    setTitle(quotation.title);
    setCurrency(quotation.currency);
    if (quotation.pricing.kind === 'fixed') {
      setAgreedAmount((quotation.pricing.amountMinor / 100).toString());
    }
  }

  return (
    <FormSheet
      open={open}
      onOpenChange={onOpenChange}
      title="New project"
      submitLabel="Save"
      submitting={createMutation.isPending}
      onSubmit={() => {
        setError(null);
        if (party.trim() === '') {
          setError('Party is required');
          return;
        }
        if (title.trim() === '') {
          setError('Title is required');
          return;
        }
        const agreedMinor = Math.round(Number(agreedAmount) * 100);
        if (!(agreedMinor > 0)) {
          setError('Agreed value must be a positive number');
          return;
        }
        const originalQuotedRange =
          selectedQuotation?.pricing.kind === 'range'
            ? {
                minMinor: selectedQuotation.pricing.minMinor,
                maxMinor: selectedQuotation.pricing.maxMinor,
              }
            : undefined;
        createMutation.mutate(
          {
            party: party.trim(),
            ...(partyId !== '' ? { partyId } : {}),
            title: title.trim(),
            currency,
            earningModel,
            agreedMinor,
            ...(originalQuotedRange !== undefined ? { originalQuotedRange } : {}),
            ...(fromQuotationId !== '' ? { fromQuotationId } : {}),
          },
          {
            onSuccess: (created) => {
              onOpenChange(false);
              navigate(`/projects/${created.id}`);
            },
            onError: (e) => {
              setError(e instanceof ApiError ? e.message : 'Could not save');
            },
          },
        );
      }}
    >
      {error !== null && <p className="mb-3 text-sm text-danger">{error}</p>}
      {candidates.length > 0 && (
        <Field
          label="From quotation (optional — QT-4 zero re-entry)"
          htmlFor="project-from-quotation"
        >
          <select
            id="project-from-quotation"
            className={selectClass}
            value={fromQuotationId}
            onChange={(e) => {
              setFromQuotationId(e.target.value);
              applyQuotation(candidates.find((q) => q.id === e.target.value));
            }}
          >
            <option value="">None</option>
            {candidates.map((q) => (
              <option key={q.id} value={q.id}>
                {q.party} — {q.title}
              </option>
            ))}
          </select>
        </Field>
      )}
      <Field label="Party (optional link)" htmlFor="project-party-id">
        <PartyPicker
          id="project-party-id"
          value={partyId}
          onChange={(id) => {
            setPartyId(id);
            const matched = (parties ?? []).find((p) => p.id === id);
            if (matched !== undefined) setParty(matched.name);
          }}
        />
      </Field>
      <Field label="Party name" htmlFor="project-party">
        <input
          id="project-party"
          className={inputClass}
          value={party}
          onChange={(e) => {
            setParty(e.target.value);
          }}
        />
      </Field>
      <Field label="Title" htmlFor="project-title">
        <input
          id="project-title"
          className={inputClass}
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
          }}
        />
      </Field>
      <Field label="Currency" htmlFor="project-currency">
        <select
          id="project-currency"
          className={selectClass}
          value={currency}
          onChange={(e) => {
            setCurrency(e.target.value as CurrencyCode);
          }}
        >
          {['PKR', 'USD', 'EUR', 'GBP'].map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Earning model" htmlFor="project-earning-model">
        <select
          id="project-earning-model"
          className={selectClass}
          value={earningModel}
          onChange={(e) => {
            setEarningModel(e.target.value as ProjectEarningModel);
          }}
        >
          {EARNING_MODEL_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </Field>
      {selectedQuotation?.pricing.kind === 'range' && (
        <p className="mb-3 text-xs text-muted">
          Quoted range{' '}
          {formatMoney({
            currency: selectedQuotation.currency,
            minorUnits: selectedQuotation.pricing.minMinor,
          })}
          –
          {formatMoney({
            currency: selectedQuotation.currency,
            minorUnits: selectedQuotation.pricing.maxMinor,
          })}{' '}
          — preserved verbatim (QT-3). Pick the agreed amount below.
        </p>
      )}
      <Field label="Agreed value" htmlFor="project-agreed">
        <AmountField
          id="project-agreed"
          currency={currency}
          value={agreedAmount}
          onChange={setAgreedAmount}
        />
      </Field>
    </FormSheet>
  );
}

export function ProjectsPage(): React.JSX.Element {
  const [status, setStatus] = useState<ProjectStatus | ''>('');
  const [partyId, setPartyId] = useState('');
  const [search, setSearch] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const { data: projects, isLoading } = useProjects({
    ...(status !== '' ? { status } : {}),
    ...(partyId !== '' ? { partyId } : {}),
  });
  const { data: parties } = useParties(false);

  const filtered = (projects ?? []).filter((p) => {
    if (search.trim() === '') return true;
    const needle = search.trim().toLowerCase();
    return (
      p.title.toLowerCase().includes(needle) ||
      (p.serviceCategory ?? '').toLowerCase().includes(needle) ||
      (p.productCategory ?? '').toLowerCase().includes(needle)
    );
  });

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-xl font-semibold text-ink">Projects</h1>
        <button
          type="button"
          className="flex items-center gap-1 rounded-[var(--radius-control)] bg-primary px-3 py-1.5 text-sm font-medium text-primary-ink hover:opacity-90 active:opacity-80"
          onClick={() => {
            setFormOpen(true);
          }}
        >
          <Plus size={16} /> New project
        </button>
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        <select
          className={`${selectClass} w-auto`}
          aria-label="Filter by status"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as ProjectStatus | '');
          }}
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <select
          className={`${selectClass} w-auto`}
          aria-label="Filter by party"
          value={partyId}
          onChange={(e) => {
            setPartyId(e.target.value);
          }}
        >
          <option value="">All parties</option>
          {(parties ?? []).map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
        <input
          className={`${inputClass} w-auto flex-1`}
          placeholder="Search title or service line…"
          aria-label="Search projects"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
          }}
        />
      </div>

      {isLoading && <p className="text-sm text-muted">Loading…</p>}

      {!isLoading && filtered.length === 0 && (
        <EmptyState
          icon={Briefcase}
          message="No projects yet."
          action={{
            label: 'New project',
            onClick: () => {
              setFormOpen(true);
            },
          }}
        />
      )}

      {filtered.length > 0 && (
        <div className="space-y-2">
          {filtered.map((p) => (
            <ProjectRow key={p.id} project={p} />
          ))}
        </div>
      )}

      <NewProjectForm open={formOpen} onOpenChange={setFormOpen} />
    </div>
  );
}
